import currency from "currency.js";
import { TFunction } from "i18next";
import moment from "moment";

import {
  GetAccount200ResponsePositionsInner,
  MarginTypeResponse,
  SideResponse,
} from "../../../../codegen-api";
import RowActionButton from "../../../../components/shared/RowActionButton";
import TickerLogo from "../../../../components/shared/TickerLogo";
import { COLORS, TEXT_COLORS } from "../../../../constants/design/colors";
import { IAccountContextType } from "../../../../contexts/AccountContext";
import { nanosToSeconds } from "../../../../utils/date";
import { getDisplayInstrumentName } from "../../../../utils/displayName";
import {
  formatCurrencyWithSubscript,
  formatSizePrecision,
} from "../../../../utils/format";
import { apiNum } from "../../../../utils/math";
import {
  formatKnownValue,
  knownPrice,
  signedTone,
} from "../../../../utils/unknownNumbers";
import { Label } from "../../../PortfolioOverviewTab/style";
import {
  Actions,
  Footer,
  Header,
  MobilePortfolioRow,
  Pill,
  PositionContent,
  Separator,
  SettlementInProgress,
  Stat,
  Stats,
  StatValue,
} from "../style";
import { pnlColor } from "./helpers";
import MobilePositionTpsl from "./MobilePositionTpsl";
import { FeatureFlagKey, isFeatureEnabled } from "../../../../config/features";

interface IMobilePositionRowProps {
  position: GetAccount200ResponsePositionsInner;
  i: number;
  positionsLength: number;
  price_precision: number;
  amount_precision: number;
  accountData: IAccountContextType["data"];
  t: TFunction;
  onClosePosition: (pos: GetAccount200ResponsePositionsInner) => void;
  setAdjustingMarginPositionId: (id: string) => void;
  setViewDetailsForPositionId: (id: string) => void;
  setShareModalPositionId: (id: string) => void;
  /** Omitted on chains without a TP/SL executor — hides the TP/SL stat. */
  setTpslModalPositionId?: (id: string) => void;
}

function MobilePositionRow({
  position,
  i,
  positionsLength,
  price_precision,
  amount_precision,
  accountData,
  t,
  onClosePosition,
  setAdjustingMarginPositionId,
  setViewDetailsForPositionId,
  setShareModalPositionId,
  setTpslModalPositionId,
}: IMobilePositionRowProps) {
  // The API's value, else size x a KNOWN mark. `Number(null)` made an
  // unresolved market's position worth $0.00.
  const mark = knownPrice(position.mark_price);
  const positionValue =
    apiNum(position.position_value) ??
    (mark !== undefined ? Number(position.amount) * mark : undefined);
  return (
    <MobilePortfolioRow
      key={position.instrument_id}
      transition={{
        delay: positionsLength > 10 ? 0 : i * 0.1,
        duration: 0.5,
        ease: "easeInOut",
      }}
      initial={{
        transform: "translateY(-8px)",
        opacity: 0,
      }}
      animate={{
        transform: "translateY(0px)",
        opacity: 1,
      }}
      exit={{
        opacity: 0,
      }}
    >
      <Header>
        <TickerLogo asset={position.asset} size={32} />
        <PositionContent>
          <div>
            <div>
              {position.option ? (
                <>
                  {position.option.option_type}
                  <Separator>{"⸱"}</Separator>
                  {moment
                    .unix(nanosToSeconds(position.option.expiry))
                    .format("DD MMM YY")}
                  <Separator>{"⸱"}</Separator>
                  {currency(position.option.strike, {
                    precision: 0,
                  }).format()}
                </>
              ) : (
                // An unresolved market arrives named by its address: show it short.
                getDisplayInstrumentName(position.instrument_name) ||
                position.instrument_name
              )}
            </div>
            {position.option &&
              moment
                .unix(nanosToSeconds(position.option.expiry))
                .isSameOrBefore(moment()) && (
                <SettlementInProgress>
                  {t("settlement_in_progress")}
                </SettlementInProgress>
              )}
            {(position as { __tradeSyncSettling?: boolean })
              .__tradeSyncSettling && (
              // Same caption, same wording as the desktop table: a close is in
              // flight that could be this row, but is not provably this row.
              // Reaches mobile because usePositionsWSS now tags settling rows —
              // this tab reads that hook, not the paginated positions hook.
              <SettlementInProgress>
                {t("close_pending", { defaultValue: "Close pending" })}
              </SettlementInProgress>
            )}
          </div>
          <Pill
            $borderRadius="8px"
            $isPositive={position.side === SideResponse.Buy}
          >
            {position.side === SideResponse.Buy
              ? t("long")
              : t("short")}
          </Pill>
        </PositionContent>
      </Header>
      <Footer>
        <Stats>
          <Stat>
            <Label>{t("size")}</Label>
            <StatValue>
              {formatSizePrecision(position.amount, amount_precision)}
            </StatValue>
          </Stat>
          <Stat>
            <Label>{t("position_value")}</Label>
            <StatValue>
              {formatKnownValue(positionValue, price_precision)}
            </StatValue>
          </Stat>
        </Stats>
        <Stats>
          <Stat>
            <Label>{t("unrealized_pnl")}</Label>
            {apiNum(position.unrealized_pnl) !== undefined ? (
              <StatValue $color={pnlColor(position.unrealized_pnl)}>
                {formatCurrencyWithSubscript(position.unrealized_pnl)}
              </StatValue>
            ) : (
              <StatValue $color={TEXT_COLORS.one}>-</StatValue>
            )}
          </Stat>
          <Stat>
            <Label>{t("realized_pnl")}</Label>
            {position.realized_pnl !== undefined &&
              position.realized_pnl !== null ? (
              <StatValue
                $color={pnlColor(String(position.realized_pnl))}
              >
                {formatCurrencyWithSubscript(position.realized_pnl)}
              </StatValue>
            ) : (
              <StatValue $color={TEXT_COLORS.one}>-</StatValue>
            )}
          </Stat>
        </Stats>
        <Stats>
          <Stat>
            <Label>
              {t("margin")}{" "}
              {position.margin_type === MarginTypeResponse.Isolated ? (
                <>
                  -{" "}
                  <span style={{ color: COLORS.blue.one }}>
                    {t("isolated")}
                  </span>
                </>
              ) : null}
            </Label>
            <StatValue>
              {/* Maintenance margin is null for an unpriced market: "-", not $0.00. */}
              {formatKnownValue(
                position.margin_type === MarginTypeResponse.Isolated
                  ? position.isolated_margin
                  : position.maintenance_margin,
                price_precision
              )}
            </StatValue>
          </Stat>
        </Stats>
        <MobilePositionTpsl
          position={position}
          price_precision={price_precision}
          t={t}
          setTpslModalPositionId={setTpslModalPositionId}
        />
      </Footer>
      <Actions>
        {isFeatureEnabled(FeatureFlagKey.ISOLATED_MARGIN_ADJUST) && (
          <RowActionButton
            variant={"edit"}
            disabled={!position.isolated_margin}
            onClick={(e) => {
              e.stopPropagation();
              setAdjustingMarginPositionId(position.instrument_id);
            }}
          />
        )}
        {!accountData?.in_liquidation && (
          <RowActionButton
            variant={"close"}
            onClick={(e) => {
              e.stopPropagation();
              onClosePosition(position);
            }}
          />
        )}
        <RowActionButton
          variant={"details"}
          onClick={(e) => {
            e.stopPropagation();
            setViewDetailsForPositionId(position.instrument_id);
          }}
        />
        <RowActionButton
          variant="share"
          svgColor={signedTone(position.unrealized_pnl, {
            up: COLORS.up.one,
            down: COLORS.down.one,
            unknown: TEXT_COLORS.three,
          })}
          onClick={(e) => {
            e.stopPropagation();
            setShareModalPositionId(
              (position as GetAccount200ResponsePositionsInner)
                .instrument_id
            );
          }}
        />
      </Actions>
    </MobilePortfolioRow>
  );
}

export default MobilePositionRow;
